/* Browse by brand (BL) — populates the empty-state panel inside the header
   predictive-search dropdown. Data comes from the "brand_name" metaobject via
   the Storefront GraphQL API, then is grouped alphabetically per the block
   settings. predictive-search.js calls window.BLBrandBrowse.ensure() when the
   search box is focused while empty. */
(function () {
  'use strict';

  var brandsPromise = null; // shared across all predictive-search instances

  /* ---------------- fetch (with cursor pagination) ---------- */
  function loadBrands(panel) {
    if (brandsPromise) return brandsPromise;

    var endpoint = panel.getAttribute('data-storefront-endpoint');
    var accessToken = panel.getAttribute('data-storefront-token');
    var filterBase = panel.getAttribute('data-filter-base') || '/collections/all';
    var filterKey = panel.getAttribute('data-filter-key') || 'filter.p.m.custom.brand_name';

    if (!endpoint || !accessToken) {
      return Promise.reject(new Error('Storefront API configuration is missing.'));
    }

    brandsPromise = fetchAllPages(endpoint, accessToken, filterBase, filterKey).then(function (list) {
      list.sort(function (a, b) { return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }); });
      return list;
    });

    return brandsPromise;
  }

  function fetchAllPages(endpoint, accessToken, filterBase, filterKey) {
    var query = [
      'query BrandMetaobjects($after: String) {',
      '  metaobjects(type: "brand_name", first: 250, after: $after) {',
      '    nodes { id handle field(key: "title") { value } }',
      '    pageInfo { hasNextPage endCursor }',
      '  }',
      '}'
    ].join('\n');
    var brands = [];

    function fetchNext(after) {
      return fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken
        },
        body: JSON.stringify({ query: query, variables: { after: after || null } })
      }).then(function (response) {
        if (!response.ok) throw new Error('Storefront API request failed (' + response.status + ').');
        return response.json();
      }).then(function (payload) {
        if (payload.errors && payload.errors.length) {
          throw new Error(payload.errors.map(function (error) { return error.message; }).join('; '));
        }

        var connection = payload.data && payload.data.metaobjects;
        if (!connection) throw new Error('Storefront API returned no brand metaobjects.');

        connection.nodes.forEach(function (brand) {
          var name = brand.field && brand.field.value ? brand.field.value : brand.handle;
          if (!name) return;
          brands.push({
            name: name,
            url: filterBase + '?' + encodeURIComponent(filterKey) + '=' + encodeURIComponent(brand.id)
          });
        });

        if (connection.pageInfo.hasNextPage) {
          return fetchNext(connection.pageInfo.endCursor);
        }
        return brands;
      });
    }

    return fetchNext(null);
  }

  function getBrands(panel) {
    return loadBrands(panel).catch(function (error) {
      brandsPromise = null;
      throw error;
    });
  }

  /* ---------------- grouping ---------------- */
  function letterOf(name) {
    var c = (String(name).trim()[0] || '#').toUpperCase();
    return /[A-Z]/.test(c) ? c : '#';
  }
  function labelOf(g) { return g.start === g.end ? g.start : g.start + '–' + g.end; }

  // Balanced: fill to ~target, break only at a letter boundary.
  function groupBalanced(list, target) {
    target = target > 0 ? target : 24;
    var out = [], cur = null;
    list.forEach(function (b) {
      var L = letterOf(b.name);
      if (cur && cur.count >= target && L !== cur.end) { out.push(cur); cur = null; }
      if (!cur) cur = { start: L, end: L, items: [], count: 0 };
      cur.end = L; cur.items.push(b); cur.count++;
    });
    if (cur) out.push(cur);
    out.forEach(function (g) { g.label = labelOf(g); });
    return out;
  }

  // Fixed: equal letter spans (5 -> A-E, F-J ...). Empty spans hidden; "#" leads.
  function groupFixed(list, size) {
    size = Math.max(1, Math.min(26, size || 5));
    var A = 65, chunks = [], hash = { start: '#', end: '#', items: [] };
    for (var s = 0; s < 26; s += size) {
      chunks.push({ start: String.fromCharCode(A + s), end: String.fromCharCode(A + Math.min(s + size - 1, 25)), items: [] });
    }
    list.forEach(function (b) {
      var L = letterOf(b.name);
      if (L === '#') { hash.items.push(b); return; }
      chunks[Math.floor((L.charCodeAt(0) - A) / size)].items.push(b);
    });
    var out = (hash.items.length ? [hash] : []).concat(chunks.filter(function (c) { return c.items.length; }));
    out.forEach(function (g) { g.label = labelOf(g); });
    return out;
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function restoreTabSelection(panel) {
    var btns = panel.querySelectorAll('.bl-bb__range');
    if (!btns.length) return;

    var active = parseInt(panel.dataset.blBbActive, 10);
    if (isNaN(active) || active < 0 || active >= btns.length) active = 0;
    for (var i = 0; i < btns.length; i++) {
      btns[i].setAttribute('aria-selected', i === active ? 'true' : 'false');
    }
  }

  /* ---------------- render ---------------- */
  function build(panel, list) {
    var rangesEl = panel.querySelector('.bl-bb__ranges');
    var listEl = panel.querySelector('.bl-bb__list');
    var msgEl = panel.querySelector('[data-bl-bb-msg]');
    if (!rangesEl || !listEl) return;

    if (msgEl) msgEl.hidden = true;

    var groups = panel.getAttribute('data-mode') === 'fixed'
      ? groupFixed(list, parseInt(panel.getAttribute('data-letters'), 10))
      : groupBalanced(list, parseInt(panel.getAttribute('data-size'), 10));

    var active = 0;

    function renderList() {
      var g = groups[active] || { items: [] };
      var html = '';
      g.items.forEach(function (b) {
        html += '<a href="' + escapeHtml(b.url) + '">' + escapeHtml(b.name) + '</a>';
      });
      listEl.innerHTML = html;
    }
    function paintTabs() {
      panel.dataset.blBbActive = String(active);
      restoreTabSelection(panel);
    }

    rangesEl.innerHTML = '';
    groups.forEach(function (g, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bl-bb__range';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.textContent = g.label;
      btn.addEventListener('click', function () { active = i; paintTabs(); renderList(); });
      rangesEl.appendChild(btn);
    });
    paintTabs();
    renderList();
  }

  /* ---------------- public hook ---------------- */
  window.BLBrandBrowse = {
    // Called by predictive-search.js on empty-focus. Lazily loads + builds once.
    ensure: function (psEl) {
      var panel = psEl.querySelector('[data-brand-browse]');
      if (!panel) return;
      if (panel.dataset.blBbReady === 'ready') {
        restoreTabSelection(panel);
        return;
      }
      if (panel.dataset.blBbReady) return;
      panel.dataset.blBbReady = 'loading';

      // Keep the input focused when clicking tabs so the dropdown doesn't close;
      // let real links (brands, "All brands") navigate normally.
      panel.addEventListener('mousedown', function (e) {
        if (!e.target.closest('a')) e.preventDefault();
      });

      getBrands(panel).then(function (list) {
        if (!list.length) {
          panel.remove();
          var input = psEl.querySelector('input[type="search"]');
          if (input && !input.value.trim()) psEl.close();
          return;
        }
        panel.dataset.blBbReady = 'ready';
        build(panel, list);
      }).catch(function () {
        panel.dataset.blBbReady = ''; // allow a retry on next focus
        var msg = panel.querySelector('[data-bl-bb-msg]');
        if (msg) { msg.hidden = false; msg.textContent = 'Couldn’t load brands.'; }
      });
    }
  };
})();
